import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Download, Upload } from 'lucide-react';
import { parse, type ParseResult } from 'papaparse';
import supabase from '../supabase';
import CreatableSelect from 'react-select/creatable';
import PhoneInput from 'react-phone-number-input';
import { CountrySelect } from './ui/country-select';
import { useWorkspace } from '../context/workspace-context';
import { SelectOption } from '../interfaces/select-option';
import { useCreateUser, useBulkImportUsers } from '../hooks/use-user';
import { getRoleId } from '../helpers/get-role-id';
import { stripPhonePrefix } from '../helpers/strip-phone-prefix';
import { BulkImportNotAllowedError, TierLimitError } from '../lib/tier-errors';
import UpgradePopup, {
  UPGRADE_REASON,
  type UpgradeReason,
} from './upgrade-popup';
import { useTier } from '../hooks/use-tier';

interface FormData {
  name: string;
  surname: string;
  email: string;
  role: string;
  status: string;
}

interface CsvUser {
  name: string;
  surname: string;
  email: string;
  phone: string;
  role: string;
  status: string;
  departments?: string;
}

export default function AddUserForm() {
  const navigate = useNavigate();
  const { currentWorkspace, isLoadingWorkspaces } = useWorkspace();
  const [file, setFile] = useState<File | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    surname: '',
    email: '',
    role: '',
    status: 'active',
  });
  const [phone, setPhone] = useState<string | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<SelectOption[]>(
    [],
  );
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<UpgradeReason | undefined>(
    undefined,
  );
  const { limits } = useTier();
  const allowsBulkImport = limits?.allowsBulkImport ?? false;

  const openUpgrade = (reason: UpgradeReason) => {
    setUpgradeReason(reason);
    setShowUpgrade(true);
  };

  const createUserMutation = useCreateUser();
  const bulkImportMutation = useBulkImportUsers();

  useEffect(() => {
    const fetchDepartments = async () => {
      if (!currentWorkspace || isLoadingWorkspaces) return;

      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .or(
          `organisation_id.is.null,organisation_id.eq.${currentWorkspace.id}`,
        );

      if (!error && data) {
        setDepartmentOptions(
          data.map((dept) => ({
            value: dept.name,
            label: dept.name,
          })),
        );
      }
    };

    fetchDepartments();
  }, [currentWorkspace, isLoadingWorkspaces]);

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | { target: { name: string; value: string } },
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!currentWorkspace || isLoadingWorkspaces) {
      return;
    }

    const phoneDigits = phone ? stripPhonePrefix(phone) : undefined;

    if (!formData.email && !phoneDigits) {
      return;
    }

    createUserMutation.mutate(
      {
        email: formData.email || undefined,
        phone: phoneDigits,
        name: formData.name,
        surname: formData.surname,
        roleId: getRoleId(formData.role),
        organisationId: currentWorkspace.id,
        departments: selectedDepartments,
      },
      {
        onSuccess: () => {
          setFormData({
            name: '',
            surname: '',
            email: '',
            role: '',
            status: 'active',
          });
          setPhone(undefined);
          setSelectedDepartments([]);
          setSuccessMessage('User added successfully!');
          setTimeout(() => {
            navigate('/users');
          }, 2000);
        },
        onError: (error) => {
          if (error instanceof TierLimitError) {
            openUpgrade(UPGRADE_REASON.USER_LIMIT);
          }
        },
      },
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFile(event.target.files[0]);
    }
  };

  const handleBulkUpload = async () => {
    if (!file) return;
    setBulkError(null);
    setSuccessMessage(null);

    if (!currentWorkspace || isLoadingWorkspaces) {
      setBulkError('Workspace not loaded');
      return;
    }

    try {
      const text = await file.text();

      parse(text, {
        header: true,
        complete: (results: ParseResult<CsvUser>) => {
          const users = results.data.map(cleanUserData);
          const validationErrors = validateCsvData(users);

          if (validationErrors.length > 0) {
            setBulkError(
              `CSV Validation Errors:\n${validationErrors.join('\n')}`,
            );
            return;
          }

          bulkImportMutation.mutate(
            {
              users: users.map((user) => ({
                email: user.email || undefined,
                phone: user.phone || undefined,
                name: user.name,
                surname: user.surname,
                roleId: getRoleId(user.role),
                organisationId: currentWorkspace.id,
                departments: user.departments
                  ? user.departments
                      .split(/[|,]/)
                      .map((department) => department.trim())
                      .filter(Boolean)
                  : [],
              })),
            },
            {
              onSuccess: (result) => {
                const fileInput = document.getElementById(
                  'file-upload',
                ) as HTMLInputElement;
                if (fileInput) fileInput.value = '';
                setFile(null);

                let successMsg = '';
                let errorMsg = '';

                if (result.successful.length > 0) {
                  successMsg = `Successfully imported ${result.successful.length} user(s)`;
                }

                if (result.failed.length > 0) {
                  errorMsg = `Failed to import ${result.failed.length} user(s):\n`;
                  result.failed.forEach(({ identifier, error }) => {
                    errorMsg += `  - ${identifier}: ${error}\n`;
                  });
                }

                if (successMsg) {
                  setSuccessMessage(successMsg);
                }

                if (errorMsg) {
                  setBulkError(errorMsg);
                } else {
                  setTimeout(() => {
                    navigate('/users');
                  }, 2000);
                }
              },
              onError: (error) => {
                setFile(null);
                if (error instanceof BulkImportNotAllowedError) {
                  openUpgrade(UPGRADE_REASON.BULK_IMPORT_NOT_ALLOWED);
                  return;
                }
                if (error instanceof TierLimitError) {
                  openUpgrade(UPGRADE_REASON.USER_LIMIT);
                }
              },
            },
          );
        },
        error: (error: Error) => {
          setBulkError(`CSV parsing error: ${error.message}`);
          setFile(null);
        },
      });
    } catch (err) {
      setBulkError(
        err instanceof Error
          ? err.message
          : 'An error occurred during bulk upload',
      );
      setFile(null);
    }
  };

  const cleanUserData = (user: CsvUser): CsvUser => {
    return {
      name: user.name?.trim() || '',
      surname: user.surname?.trim() || '',
      email:
        user.email
          ?.trim()
          .toLowerCase()
          .replace(/[<>'"]/g, '') || '',
      phone: user.phone?.trim().replace(/\D/g, '') || '',
      role: user.role?.trim().toLowerCase() || '',
      status: user.status?.trim().toLowerCase() || '',
      departments: user.departments?.trim() || '',
    };
  };

  const validateCsvData = (data: CsvUser[]): string[] => {
    const errors: string[] = [];
    const validRoles = ['admin', 'employee'];
    const validStatuses = ['active', 'inactive'];

    data.forEach((user, index) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^\d{10,15}$/;

      if (!user.email && !user.phone) {
        errors.push(`Row ${index + 1}: Either email or phone is required`);
      }
      if (user.email && !emailRegex.test(user.email)) {
        errors.push(`Row ${index + 1}: Invalid email format - "${user.email}"`);
      }
      if (user.phone && !phoneRegex.test(user.phone)) {
        errors.push(
          `Row ${index + 1}: Invalid phone format - use country code + number (e.g. 27821234567)`,
        );
      }
      if (!user.name) {
        errors.push(`Row ${index + 1}: Name is required`);
      }
      if (!user.surname) {
        errors.push(`Row ${index + 1}: Surname is required`);
      }
      if (!validRoles.includes(user.role?.toLowerCase())) {
        errors.push(
          `Row ${index + 1}: Invalid role (must be Admin or Employee)`,
        );
      }
      if (!validStatuses.includes(user.status?.toLowerCase())) {
        errors.push(
          `Row ${index + 1}: Invalid status (must be Active or Inactive)`,
        );
      }
    });

    return errors;
  };

  const downloadTemplate = () => {
    const headers = ['name,surname,email,phone,role,status,departments'];
    const example = [
      'John,Doe,john@example.com,27821234567,employee,active,Sales|Marketing',
    ];
    const csv = headers.concat(example).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_template.csv';
    a.click();
  };

  const handleDepartmentChange = (newValue: readonly SelectOption[] | null) => {
    if (!newValue) {
      setSelectedDepartments([]);
      return;
    }
    setSelectedDepartments(newValue.map((option) => option.value));
  };

  return (
    <>
      <Card className="w-full bg-white shadow-none">
        <CardContent className="p-6">
          <Tabs defaultValue="single" className="w-full">
            <TabsList
              className={`grid w-full ${allowsBulkImport ? 'grid-cols-2' : 'grid-cols-1'} mb-4`}
            >
              <TabsTrigger value="single">Add Single User</TabsTrigger>
              {allowsBulkImport && (
                <TabsTrigger value="bulk">Bulk Import</TabsTrigger>
              )}
            </TabsList>
            <TabsContent
              value="single"
              className="mt-0 data-[state=active]:block"
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">First Name</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John"
                      required
                      className="h-12 bg-white border border-gray-300 rounded-md focus:border-accent-purple focus:ring-accent-purple focus:outline-none"
                      style={{ fontSize: '16px' }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="surname">Last Name</Label>
                    <Input
                      id="surname"
                      name="surname"
                      type="text"
                      value={formData.surname}
                      onChange={handleInputChange}
                      placeholder="Doe"
                      required
                      className="h-12 bg-white border border-gray-300 rounded-md focus:border-accent-purple focus:ring-accent-purple focus:outline-none"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email{' '}
                    <span className="text-gray-400 font-normal">
                      (optional if phone provided)
                    </span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="you@example.com"
                    className="h-12 bg-white border border-gray-300 rounded-md focus:border-accent-purple focus:ring-accent-purple focus:outline-none"
                    style={{ fontSize: '16px' }}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">
                    Phone{' '}
                    <span className="text-gray-400 font-normal">
                      (optional if email provided)
                    </span>
                  </Label>
                  <PhoneInput
                    id="phone"
                    defaultCountry="ZA"
                    international
                    countryCallingCodeEditable={false}
                    value={phone}
                    onChange={setPhone}
                    placeholder="79 123 4567"
                    inputComponent={Input}
                    countrySelectComponent={CountrySelect}
                    numberInputProps={{
                      className:
                        'h-12 bg-white border border-gray-300 rounded-md focus:border-accent-purple focus:ring-accent-purple focus:outline-none',
                      style: { fontSize: '14px' },
                    }}
                    className="flex gap-2 items-center"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select
                      value={formData.role}
                      onValueChange={(value) =>
                        handleSelectChange('role', value)
                      }
                      required
                    >
                      <SelectTrigger className="h-12 bg-white border border-gray-300 rounded-md focus:border-accent-purple focus:ring-accent-purple focus:outline-none">
                        <SelectValue placeholder="Select Role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Admin">Admin</SelectItem>
                        <SelectItem value="Employee">Employee</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) =>
                        handleSelectChange('status', value)
                      }
                      required
                    >
                      <SelectTrigger className="h-12 bg-white border border-gray-300 rounded-md focus:border-accent-purple focus:ring-accent-purple focus:outline-none">
                        <SelectValue placeholder="Select Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="departments">Team / Department</Label>
                  <CreatableSelect
                    isMulti
                    options={departmentOptions}
                    value={selectedDepartments.map((dept) => ({
                      value: dept,
                      label: dept,
                    }))}
                    onChange={handleDepartmentChange}
                    className="basic-multi-select"
                    classNamePrefix="select"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-accent-purple hover:bg-accent-purple/90 text-white rounded-full py-6 flex items-center justify-center"
                  disabled={createUserMutation.isPending}
                >
                  {createUserMutation.isPending ? 'Adding User...' : 'Add User'}
                </Button>

                {createUserMutation.error &&
                  !(createUserMutation.error instanceof TierLimitError) && (
                    <div className="p-3 text-sm text-center text-destructive">
                      {createUserMutation.error instanceof Error
                        ? createUserMutation.error.message
                        : 'An error occurred'}
                    </div>
                  )}

                {successMessage && (
                  <div className="p-3 text-sm text-center text-green-800">
                    {successMessage}
                  </div>
                )}
              </form>
            </TabsContent>
            {allowsBulkImport && (
              <TabsContent
                value="bulk"
                className="mt-0 data-[state=active]:block"
              >
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="file-upload">Upload CSV File</Label>
                    <Input
                      id="file-upload"
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                    />
                  </div>
                  {successMessage && (
                    <div className="p-3 text-sm text-left text-green-800 whitespace-pre-line">
                      {successMessage}
                    </div>
                  )}
                  {(bulkError ||
                    (bulkImportMutation.error &&
                      !(
                        bulkImportMutation.error instanceof TierLimitError
                      ))) && (
                    <div className="p-3 text-sm text-left text-destructive whitespace-pre-line">
                      {bulkError ||
                        (bulkImportMutation.error instanceof Error
                          ? bulkImportMutation.error.message
                          : 'An error occurred')}
                    </div>
                  )}
                  <Button
                    onClick={handleBulkUpload}
                    className="w-full h-12 bg-accent-purple hover:bg-accent-purple/90 text-white rounded-full py-6 flex items-center justify-center"
                    disabled={!file || bulkImportMutation.isPending}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {bulkImportMutation.isPending
                      ? 'Importing Users...'
                      : 'Upload and Import Users'}
                  </Button>
                  <Button
                    onClick={downloadTemplate}
                    className="w-full h-12 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full py-6 flex items-center justify-center"
                  >
                    <Download className="mr-2 h-4 w-4" /> Download Template
                  </Button>
                </div>
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>
      <UpgradePopup
        open={showUpgrade}
        onOpenChange={setShowUpgrade}
        reason={upgradeReason}
      />
    </>
  );
}
